import { RecordingRecoveryHost, type RecoveryVerdict } from "@msuite/ui/video-recorder";
import { ResponseError } from "@msuite/utils/errors";
import { useCallback } from "react";
import { useAuth } from "@/hooks/use-auth";
import { client } from "@/hono";
import { recordingOwner } from "./recording-owner";
import { verdictForVideo } from "./recovery-verdict";

/**
 * Asks a video row what became of it while its recording sat on disk.
 *
 * The error toast is suppressed and the thrown status read instead, because here a 404 is an
 * answer rather than a failure — and a request that never reached the server at all must leave the
 * recording exactly where it is.
 */
async function inspectVideo(videoId: string): Promise<RecoveryVerdict> {
	try {
		const response = await client.videos[":videoId"].$get({ param: { videoId } });
		const payload = await response.json();
		if (!response.ok || !("video" in payload)) return verdictForVideo(response.status, null);
		return verdictForVideo(200, payload.video.status);
	} catch (error) {
		if (error instanceof ResponseError) return verdictForVideo(error.status, null);
		return { kind: "unknown" };
	}
}

/**
 * The application's mount for the recovery question. It exists to supply the three things
 * `@msuite/ui` cannot know: who is signed in, how to ask the server about a video, and how to
 * ship or remove one.
 */
export function RecordingRecovery() {
	/** Hooks */
	const { organization, user } = useAuth();

	/** Computed */
	const orgId = organization.id;
	const userId = user?.id;
	const owner = orgId && userId ? recordingOwner(orgId, userId) : null;

	/** Functions */
	const remove = useCallback(async (videoId: string) => {
		return await client.videos[":videoId"].$delete({ param: { videoId } });
	}, []);

	/** Render */
	return (
		<RecordingRecoveryHost
			owner={owner}
			inspect={inspectVideo}
			remove={remove}
			toTarget={(recording) => ({
				videoId: recording.videoId,
				label: recording.label,
				href: recording.href,
				owner: owner ?? undefined,
				// Imported on the send rather than at the top: the module pulls in the chunked
				// uploader, and this component is mounted eagerly in the app shell while the wiki
				// it belongs to is a lazily loaded route.
				upload: async (blob, onProgress) => {
					const { uploadRecording } = await import("@/sites/wiki/prepare-recording");
					await uploadRecording(recording.videoId)(blob, onProgress);
				},
				abandon: () => remove(recording.videoId),
			})}
		/>
	);
}
